import { useEffect } from 'react';
import clsx from 'clsx';
import { useInboxStore } from '../../stores/inbox-store';
import type { SuggestedMatch } from '../../stores/inbox-store';
import { useInboxDrawerStore } from '../../stores/inbox-drawer-store';
import { KlantinfoRail } from './KlantinfoRail';
import { KlantSuggesties } from './KlantSuggesties';

/**
 * Slide-out wrapper rond bestaande KlantinfoRail.
 * Default dicht — toggle via store.toggleKlantinfo() of Cmd/Ctrl+I.
 *
 * Gedrag:
 * - Backdrop-klik = sluiten
 * - Esc = sluiten (alleen als geen input focus)
 * - Slide-in van rechts (Tailwind translate-x transition)
 *
 * Geconsumeerd door ThreadDetail / nieuwe split-screen InboxPage in batch D4.
 */
export function KlantinfoSlideOut() {
  const klantinfoOpen = useInboxStore((s) => s.klantinfoOpen);
  const toggleKlantinfo = useInboxStore((s) => s.toggleKlantinfo);
  const activeThread = useInboxStore((s) => s.activeThread);
  const customer = activeThread?.customer ?? null;
  const drawerOpenItem = useInboxDrawerStore((s) => s.openItem);

  // Esc-handler: sluit slide-out tenzij gebruiker in input zit (laat input zelf blur'en)
  useEffect(() => {
    if (!klantinfoOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;
      e.preventDefault();
      toggleKlantinfo();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [klantinfoOpen, toggleKlantinfo]);

  if (!activeThread) return null;

  const conv = activeThread.conversation;
  const customerLinked = customer?.found === true;
  const suggestions: SuggestedMatch[] = !customerLinked
    ? (conv.suggested_customer_ids ?? []).slice(0, 5)
    : [];

  // Niets te tonen: geen klant + geen suggesties → originele null-render behouden
  // zodat slide-out niet onnodig opent.
  if (!customerLinked && suggestions.length === 0) return null;


  return (
    <>
      {/* Backdrop — alleen klikbaar als geopend */}
      <div
        onClick={toggleKlantinfo}
        className={clsx(
          'fixed inset-0 bg-slate-900/20 z-40 transition-opacity',
          klantinfoOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
        )}
        aria-hidden="true"
      />

      {/* Panel — slide-in van rechts */}
      <aside
        className={clsx(
          'fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-xl z-50',
          'transition-transform duration-200 ease-out',
          'overflow-y-auto',
          klantinfoOpen ? 'translate-x-0' : 'translate-x-full',
        )}
        aria-hidden={!klantinfoOpen}
      >
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-900">Klantinfo</h2>
          <button
            onClick={toggleKlantinfo}
            className="text-slate-400 hover:text-slate-600 p-1"
            aria-label="Sluit klantinfo"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-2">
          {customerLinked && customer && (
            <KlantinfoRail customer={customer} activeId={drawerOpenItem?.id} />
          )}
          {!customerLinked && suggestions.length > 0 && (
            <KlantSuggesties conversationId={conv.id} suggestions={suggestions} />
          )}
        </div>
      </aside>
    </>
  );
}
